import { useEffect, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { format } from "date-fns";
import { saveAs } from "file-saver";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  applyReportTemplate,
  getReportTemplates as getReportTemplatesService,
} from "services/ReportTemplates.services";
import {
  continueSession as continueSessionService,
  exportSession as exportSessionService,
  getSession,
  summarizeSession as summarizeSessionService,
  updateSession as updateSessionService,
  uploadScanedDocument as uploadScanedDocumentService,
} from "services/Session.services";
import { toast } from "sonner";
import z from "zod";

import type { ApplyReportTemplateResponse } from "types/ReportTemplates.type";
import type { ExportSessionResponse, Session, SessionUpdateRequest } from "types/Session.type";

import { ROUTES } from "utils/constants";
import { queryClient } from "utils/queryClient";

import { buildSummarizeSchema } from "./SessionSumarize.schema";

// Extend Navigator to include msSaveOrOpenBlob for IE support
interface NavigatorWithMsSaveOrOpenBlob extends Navigator {
  msSaveOrOpenBlob?: (blob: Blob, defaultName?: string) => boolean;
}

interface UploadScanedDocumentResponse {
  scan: string;
}

export function useSessionSummarize() {
  // ====================================================================================
  // HOOKS
  // ====================================================================================
  const { id } = useParams();
  const calledRef = useRef(false);
  const navigate = useNavigate();
  //const { t } = useTranslation();
  const summarizeSessionSchema = buildSummarizeSchema(/* t */);

  // ====================================================================================
  // STATE
  // ====================================================================================
  const [open, setOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);

  // ====================================================================================
  // GET SESSION
  // ====================================================================================
  const { data: session, isLoading: isSessionLoading } = useQuery<Session>({
    queryKey: ["session", id],
    queryFn: () => getSession(id!),
    enabled: !!id,
  });

  // ====================================================================================
  // SUMMARIZE SESSION
  // ====================================================================================
  const {
    mutate: summarizeSession,
    isPending: isSummarizing,
    isSuccess: isSummarizingSuccess,
  } = useMutation<Session, Error, string>({
    mutationFn: (sessionId) => summarizeSessionService(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session", id] });
    },
    onError: () => {
      navigate(ROUTES.HOME);
    },
  });

  // ====================================================================================
  // UPDATE SESSION
  // ====================================================================================
  const {
    mutate: updateSession,
    mutateAsync: updateSessionAsync,
    //isPending: isUpdating,
    isSuccess: isUpdatingSuccess,
  } = useMutation<Session, Error, SessionUpdateRequest>({
    mutationFn: (session) => updateSessionService(id!, session),
    onError: (error) => {
      const message =
        isAxiosError<{ message?: string }>(error) && error.response?.data?.message
          ? error.response.data.message
          : "Failed to update session.";
      toast.error(message);
    },
  });

  // ====================================================================================
  // SCAN
  // ====================================================================================
  const {
    mutate: uploadScanedDocument,
    mutateAsync: uploadScanedDocumentAsync,
    isPending: isUploadingScanedDocument,
    isSuccess: isUploadingScanedDocumentSuccess,
    reset: resetUploadScanedDocument,
  } = useMutation<UploadScanedDocumentResponse, Error, File | Blob>({
    mutationFn: (doc: File | Blob) => uploadScanedDocumentService(id!, doc),
    onSuccess: (data) => {
      form.setValue("uploadedReport", data.scan);
    },
  });

  // ====================================================================================
  // EXPORT SESSION
  // ====================================================================================
  const { mutate: exportSession, isPending: isExporting } = useMutation<ExportSessionResponse, Error, string>({
    mutationFn: (sessionId) => exportSessionService(sessionId),
    onSuccess: (data) => {
      try {
        const blob = data.data as Blob;

        const contentDisposition = data.headers?.["content-disposition"] || "";
        const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
        const filename = filenameMatch ? filenameMatch[1] : "session.docx";

        const navigatorWithMsSaveOrOpenBlob = window.navigator as NavigatorWithMsSaveOrOpenBlob;
        if (typeof navigatorWithMsSaveOrOpenBlob.msSaveOrOpenBlob !== "undefined") {
          navigatorWithMsSaveOrOpenBlob.msSaveOrOpenBlob?.(blob, filename);
        } else if (typeof saveAs === "function") {
          saveAs(blob, filename);
        } else {
          const url = window.URL.createObjectURL(blob);
          window.open(url, "_blank");
          setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        }
      } catch {
        toast.error("File download is not supported on this device/browser.");
      }
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      navigate(ROUTES.HOME);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      navigate(ROUTES.HOME);
    },
  });

  // ====================================================================================
  // CONTINUE SESSION
  // ====================================================================================
  const { mutate: continueSession } = useMutation<Session, Error, string>({
    mutationFn: () => continueSessionService(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session", id] });
    },
  });

  // ====================================================================================
  // APPLY TEMPLATE
  // ====================================================================================

  const { mutate: applyTemplate, isPending: isApplyingTemplate } = useMutation<
    ApplyReportTemplateResponse,
    Error,
    string
  >({
    mutationFn: async (sessionId) => {
      const values = form.getValues();
      return await applyReportTemplate(sessionId, values.report);
    },
    onSuccess: (data) => {
      form.setValue("report", data.content);
    },
    onError: () => {
      toast.error("Failed to save report.");
    },
  });

  // ====================================================================================
  // FORM
  // ====================================================================================

  // INIT FORM
  const form = useForm({
    resolver: zodResolver(summarizeSessionSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      birthDate: undefined,
      address: "",
      phone: "",
      email: "",
      identificationNumber: "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // EXTRACT FORM VARS
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = form;

  // SUBMIT
  function onSubmit(values: z.infer<typeof summarizeSessionSchema>) {
    if (!session?.sessionPatient?.id) {
      throw new Error("Session patient ID is missing.");
    }
    const reportId = session.sessionReport?.id ?? session.sessionReportId;
    if (!reportId) {
      toast.error("Session report ID is missing.");
      return;
    }
    const patientUpdate = {
      id: session.sessionPatient.id,
      firstName: values.firstName,
      lastName: values.lastName,
      birthDate: values.birthDate ? format(new Date(values.birthDate), "yyyy-MM-dd") : undefined,
      address: values.address,
      phone: values.phone,
      email: values.email,
      identificationNumber: values.identificationNumber,
    };

    if (isDirty)
      updateSession({
        sessionPatientUpdateRequest: patientUpdate,
        //sessionCategoryUpdateRequests: categoryUpdate,
        sessionReportUpdateRequest: {
          id: reportId,
          htmlReport: values.report,
        },
      });
    else if (session && session.id) {
      exportSession(String(session.id));
    }
  }

  async function onSubmitPause(values: z.infer<typeof summarizeSessionSchema>) {
    if (!session?.sessionPatient?.id) {
      throw new Error("Session patient ID is missing.");
    }
    const reportId = session.sessionReport?.id ?? session.sessionReportId;
    if (!reportId) {
      toast.error("Session report ID is missing.");
      return;
    }
    const patientUpdate = {
      id: session.sessionPatient.id,
      firstName: values.firstName,
      lastName: values.lastName,
      birthDate: values.birthDate ? format(new Date(values.birthDate), "yyyy-MM-dd") : undefined,
      address: values.address,
      phone: values.phone,
      email: values.email,
      identificationNumber: values.identificationNumber,
    };

    await updateSessionAsync({
      sessionPatientUpdateRequest: patientUpdate,
      sessionReportUpdateRequest: {
        id: reportId,
        htmlReport: values.report,
      },
    });
  }

  // ====================================================================================
  // ADDITIONAL QUERIES
  // ====================================================================================
  const reportTemplates = useQuery({
    queryKey: ["reportTemplates"],
    queryFn: getReportTemplatesService,
  });

  // ====================================================================================
  // HANDLERS
  // ====================================================================================
  const handleStopSession = async () => {
    await onSubmitPause(form.getValues());

    if (id) {
      await queryClient.cancelQueries({ queryKey: ["session", id] });
      queryClient.removeQueries({ queryKey: ["session", id], exact: true });
    }

    await queryClient.invalidateQueries({ queryKey: ["sessions"], refetchType: "none" });
    navigate(ROUTES.HOME);
  };

  const handleContinueSession = () => {
    continueSession(String(session?.id));
  };

  // ====================================================================================
  // EFFECTS
  // ====================================================================================
  useEffect(() => {
    if (isSummarizing) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isSummarizing]);

  useEffect(() => {
    if (isExporting) {
      setExportOpen(true);
    }
  }, [isExporting]);

  useEffect(() => {
    if (isUpdatingSuccess && session && session.id !== undefined) {
      exportSession(String(session.id));
    }
  }, [isUpdatingSuccess, exportSession, session]);

  useEffect(() => {
    if (session) {
      form.reset({
        firstName: session.sessionPatient?.firstName,
        lastName: session.sessionPatient?.lastName,
        birthDate: session.sessionPatient?.birthDate ? new Date(session.sessionPatient.birthDate) : undefined,
        address: session.sessionPatient?.address || "",
        phone: session.sessionPatient?.phone || "",
        email: session.sessionPatient?.email || "",
        identificationNumber: session.sessionPatient?.identificationNumber || "",
        report: session.sessionReport.htmlReport,
      });
      form.trigger();
    }
  }, []);

  useEffect(() => {
    if (session && session.sessionStatusId === 2 && !isSummarizing && !isSummarizingSuccess && !calledRef.current) {
      calledRef.current = true;
      summarizeSession(String(session.id));
    }
  }, [session, isSummarizing, isSummarizingSuccess, summarizeSession]);

  return {
    isSessionLoading,
    form,
    isValid,
    control,
    handleSubmit,
    onSubmit,
    handleContinueSession,
    setPauseOpen,
    pauseOpen,
    handleStopSession,
    exportOpen,
    setExportOpen,
    isSummarizing,
    isExporting,
    open,
    reportTemplates: reportTemplates.data?.resultList,
    applyTemplate,
    isApplyingTemplate,
    uploadScanedDocument,
    uploadScanedDocumentAsync,
    isUploadingScanedDocument,
    isUploadingScanedDocumentSuccess,
    resetUploadScanedDocument,
  };
}
